/**
 * Small shared helpers for the Resume Builder panels.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

export const base64ToBlobUrl = (b64) => {
  const bytes = Uint8Array.from(atob(b64), ch => ch.charCodeAt(0));
  return URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
};

export const errMsg = (e, fallback) => e?.response?.data?.message || e?.response?.data?.error || fallback;

/** 402 = out of AI tokens: the global axios interceptor already opens the purchase screen. */
export const isTokenError = (e) => e?.response?.status === 402;

/** GET a PDF with auth and save it under `fileName`. */
export async function downloadPdf(api, url, fileName) {
  const r = await api.get(url, { responseType: 'blob' });
  const href = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
  const a = document.createElement('a');
  a.href = href; a.download = fileName;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(href), 5000);
}

/**
 * Server-rendered PDF preview. `render()` must return base64. Debounced auto-refresh when
 * `deps` change (pass the editor state), ignoring out-of-order responses.
 */
export function usePdfPreview(render, deps, { auto = true, delay = 1200, enabled = true } = {}) {
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const seq = useRef(0);
  const renderRef = useRef(render);
  renderRef.current = render;

  const refresh = useCallback(async () => {
    const my = ++seq.current;
    setLoading(true); setError(null);
    try {
      const b64 = await renderRef.current();
      if (my !== seq.current) return;
      if (!b64) { setUrl(prev => { if (prev) URL.revokeObjectURL(prev); return null; }); return; }
      const next = base64ToBlobUrl(b64);
      setUrl(prev => { if (prev) URL.revokeObjectURL(prev); return next; });
    } catch (e) {
      if (my === seq.current) setError(errMsg(e, 'Preview failed'));
    } finally {
      if (my === seq.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!auto || !enabled) return undefined;
    const t = setTimeout(refresh, delay);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auto, enabled, refresh, ...deps]);

  useEffect(() => () => { setUrl(prev => { if (prev) URL.revokeObjectURL(prev); return null; }); }, []);

  return { url, loading, error, refresh };
}

export const fmtDateTime = (d) => {
  if (!d) return '';
  try { return new Date(d).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }); }
  catch { return String(d); }
};
